import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Home, Building2, Info, MessageSquare, User } from 'lucide-react';
import { Link, useLocation } from "react-router-dom";
import { useBranding } from "@/contexts/BrandingContext";
import { useTenant } from "@/contexts/TenantContext";
import { FavoritesButton } from "@/components/FavoritesButton";
import { generateWebPWithFallback } from "@/utils/imageUtils";
import { useSitePages } from "@/hooks/useSitePages";

interface MainHeaderProps {
  avatarUrl?: string;
  displayName?: string;
  hidePropertiesLink?: boolean;
  transparentVariant?: 'none' | 'light' | 'pill';
  forceSolid?: boolean;
}

export const MainHeader = ({ 
  avatarUrl, 
  displayName, 
  hidePropertiesLink = false,
  transparentVariant = 'none',
  forceSolid = false
}: MainHeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { branding } = useBranding();
  const { tenant } = useTenant();
  const location = useLocation();
  const { data: sitePages = [] } = useSitePages(tenant?.id || null);

  // Handle scroll detection for transparent header
  useEffect(() => {
    if (transparentVariant === 'none') return;
    
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Check initial position
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [transparentVariant]);

  // Gera URLs baseadas no tenant slug ou subdomínio
  const getUrl = (path: string) => {
    const hostname = window.location.hostname;
    const tenantDomain = 'imobpanda.site';
    const cleanHost = hostname.replace(/^www\./, '');
    
    if (cleanHost !== tenantDomain && cleanHost.endsWith(tenantDomain)) {
      return path === '' ? '/' : path;
    }
    
    if (tenant?.subdomain) {
      return `/${tenant.subdomain}${path === '' ? '' : path}`;
    }
    
    return path === '' ? '/marketplace' : path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isCurrentPath = (path: string) => {
    const currentPath = location.pathname;
    const navPath = getUrl(path);
    
    if (path === '' || path === '/') {
      return currentPath === navPath || currentPath === getUrl('/');
    }
    
    return currentPath.startsWith(navPath);
  };

  if (!branding) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="backdrop-blur-lg bg-background/80 border-b border-border/50 shadow-sm">
          <header className="w-full max-w-7xl mx-auto py-3 px-4 sm:px-6 flex items-center justify-between my-0 lg:px-8 lg:py-[16px]">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
            </div>
            <div className="hidden lg:flex items-center gap-4">
              <div className="h-10 w-40 bg-muted animate-pulse rounded-xl" />
            </div>
          </header>
        </div>
      </div>
    );
  }

  // Determine which visual style to show based on Branding settings

  const isCurrentlyTransparent = !forceSolid && transparentVariant !== 'none' && !isScrolled && !mobileMenuOpen;

  const headerBgClass = isCurrentlyTransparent 
    ? "bg-transparent border-transparent text-white" 
    : "backdrop-blur-xl bg-background/85 dark:bg-background/80 border border-border/40 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] text-foreground";

  return (
    <div className={`fixed left-0 right-0 z-50 transition-all duration-500 px-3 sm:px-6 flex justify-center pointer-events-none ${isScrolled ? 'top-2 sm:top-3' : 'top-3 sm:top-5'}`}>
      <div className={`w-full max-w-6xl transition-all duration-500 pointer-events-auto rounded-2xl sm:rounded-[2rem] ${headerBgClass}`} style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="w-full py-2.5 px-4 sm:px-5 flex items-center justify-between lg:px-6 lg:py-3">
          
          <div className={`flex items-center gap-4 flex-shrink-0 transition-all duration-300 opacity-100`}>
            <Link to={getUrl('')} className="flex items-center gap-3 sm:gap-4 group">
              {branding.header_style !== 'realtor' ? (
                <>
                  {(branding.header_style === 'logo' || branding.header_style === 'logo_only' || !branding.header_style) && branding.header_logo_url && (
                    <img
                      src={generateWebPWithFallback(branding.header_logo_url, 90, 300, 80).webp || branding.header_logo_url}
                      alt={branding.header_text || branding.display_name || displayName || "Logo"}
                      className="h-8 sm:h-10 object-contain"
                      loading="eager"
                      decoding="async"
                    />
                  )}
                  {(branding.header_style === 'logo' || branding.header_style === 'text_only' || (!branding.header_style && (!branding.header_logo_url || branding.header_text))) && (branding.header_text || branding.display_name) && (
                    <span className="font-bold text-base sm:text-xl truncate max-w-[160px] sm:max-w-none tracking-tight transition-colors">
                      {branding.header_text || branding.display_name}
                    </span>
                  )}
                </>
              ) : (
                <>
                  {avatarUrl ? (
                    <img
                      src={generateWebPWithFallback(avatarUrl, 85, 128, 128).webp}
                      alt={displayName || "Corretor"}
                      className="h-10 w-10 sm:h-12 sm:w-12 object-cover rounded-xl border border-border/50 group-hover:border-[var(--brand-primary)] group-hover:shadow-md transition-all shadow-sm"
                      width="48"
                      height="48"
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-muted/50 flex items-center justify-center border border-border/50 group-hover:border-[var(--brand-primary)] group-hover:shadow-md transition-all shadow-sm">
                      <User className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground/70" />
                    </div>
                  )}
                  {displayName && (
                    <div className="flex flex-col justify-center">
                      <span className="font-bold text-base sm:text-lg truncate max-w-[160px] sm:max-w-none tracking-tight group-hover:text-[var(--brand-primary)] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {displayName}
                      </span>
                      <span className="text-[10px] sm:text-xs text-muted-foreground font-medium uppercase tracking-wider">
                        Corretor de Imóveis
                      </span>
                    </div>
                  )}
                </>
              )}
            </Link>
          </div>
          
          <div className="lg:hidden flex items-center gap-2">
            <FavoritesButton />
            
            <button
              className={`p-2 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center ${isCurrentlyTransparent ? 'text-white hover:bg-white/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          
          <nav className="hidden lg:flex items-center">
            <div className="flex items-center gap-2">
              <Link to={getUrl('')} className={`px-4 py-2 rounded-full transition-all duration-200 text-sm flex items-center ${
                isCurrentlyTransparent 
                  ? (isCurrentPath('') ? 'bg-white/20 text-white font-semibold' : 'text-white/80 hover:text-white hover:bg-white/10')
                  : (isCurrentPath('') ? 'bg-muted/60 text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40')
              }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                Início
              </Link>
              
              {!hidePropertiesLink && (
                <Link to={getUrl('/properties')} className={`px-4 py-2 rounded-full transition-all duration-200 text-sm flex items-center ${
                  isCurrentlyTransparent 
                    ? (isCurrentPath('/properties') ? 'bg-white/20 text-white font-semibold' : 'text-white/80 hover:text-white hover:bg-white/10')
                    : (isCurrentPath('/properties') ? 'bg-muted/60 text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40')
                }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                  Imóveis
                </Link>
              )}

              <Link to={getUrl('/financiamento')} className={`px-4 py-2 rounded-full transition-all duration-200 text-sm flex items-center ${
                isCurrentlyTransparent 
                  ? (isCurrentPath('/financiamento') ? 'bg-white/20 text-white font-semibold' : 'text-white/80 hover:text-white hover:bg-white/10')
                  : (isCurrentPath('/financiamento') ? 'bg-muted/60 text-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40')
              }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                Financiamento
              </Link>

              {/* Render dynamic sections as anchor links */}
              {sitePages.filter(p => p.slug !== 'home').map(page => (
                <Link key={page.id} to={getUrl(`/#${page.slug}`)} className={`px-4 py-2 rounded-full transition-all duration-200 text-sm flex items-center ${
                  isCurrentlyTransparent 
                    ? 'text-white/80 hover:text-white hover:bg-white/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                  {page.title}
                </Link>
              ))}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <FavoritesButton />
            <Button
              variant="default"
              className={`py-2 text-sm font-medium transition-all duration-200 min-h-[42px] flex items-center justify-center hover:opacity-90 hover:scale-105 active:scale-95 pl-5 pr-1.5 rounded-full gap-3 border ${
                isCurrentlyTransparent
                  ? 'border-white/40 bg-transparent text-white hover:bg-white/10 shadow-none'
                  : 'border-transparent shadow-md shadow-black/10'
              }`}
              style={
                !isCurrentlyTransparent
                  ? { backgroundColor: 'var(--brand-primary)', color: 'white' }
                  : undefined
              }
            >
              Entrar em Contato
              <div 
                className="rounded-full w-7 h-7 flex items-center justify-center shadow-sm"
                style={{ backgroundColor: 'white' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isCurrentlyTransparent ? '#000000' : 'var(--brand-primary)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </div>
            </Button>
          </div>
          
          {mobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-4 right-4 bg-white dark:bg-[#131316] py-4 px-6 border border-border/50 rounded-2xl shadow-xl mt-2 z-50 text-foreground">
              <div className="flex flex-col gap-2">
                <Link 
                  to={getUrl('')} 
                  className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors min-h-[44px] flex items-center ${
                    isCurrentPath('') 
                      ? 'text-white font-semibold' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  style={isCurrentPath('') ? { backgroundColor: "var(--brand-primary)" } : {}}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Home size={18} className="mr-3" style={{ color: isCurrentPath('') ? "white" : "var(--brand-primary)" }} /> 
                  Início
                </Link>
                
                {!hidePropertiesLink && (
                  <Link 
                    to={getUrl('/properties')} 
                    className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors min-h-[44px] flex items-center ${
                      isCurrentPath('/properties') 
                        ? 'text-white font-semibold' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                    style={isCurrentPath('/properties') ? { backgroundColor: "var(--brand-primary)" } : {}}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building2 size={18} className="mr-3" style={{ color: isCurrentPath('/properties') ? "white" : "var(--brand-primary)" }} /> 
                    Imóveis
                  </Link>
                )}

                <Link 
                  to={getUrl('/financiamento')} 
                  className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors min-h-[44px] flex items-center ${
                    isCurrentPath('/financiamento') 
                      ? 'text-white font-semibold' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  style={isCurrentPath('/financiamento') ? { backgroundColor: "var(--brand-primary)" } : {}}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isCurrentPath('/financiamento') ? "white" : "var(--brand-primary)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3"><path d="M2 12h20"/><path d="M12 2v20"/><path d="m19 15-7-7-7 7"/></svg>
                  Financiamento
                </Link>

                {sitePages.filter(p => p.slug !== 'home').map(page => (
                  <Link 
                    key={page.id}
                    to={getUrl(`/#${page.slug}`)} 
                    className="px-4 py-3 text-sm font-medium rounded-xl transition-colors min-h-[44px] flex items-center text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Info size={18} className="mr-3" style={{ color: "var(--brand-primary)" }} /> 
                    {page.title}
                  </Link>
                ))}
                
                <div className="mt-2 pt-2 border-t border-border/50">
                  <Button
                    variant="default"
                    className="w-full border-0 px-6 py-4 text-sm font-medium rounded-xl shadow-md transition-all duration-200"
                    style={{ backgroundColor: "var(--brand-primary)", color: "white" }}
                  >
                    Entrar em Contato
                  </Button>
                </div>
              </div>
            </div>
          )}
        </header>
      </div>
    </div>
  );
};
